import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface CorkBoardItem {
  id: string;
  type: 'note' | 'photo';
  content?: string;
  photo_url?: string;
  caption?: string;
  position_x: number;
  position_y: number;
  z_index: number;
  rotation: number;
  is_locked?: boolean;
  color?: string;
}

interface GroupPage {
  group_name: string;
  created_at: string;
  item_count: number;
  password: string | null;
}

export default function AdminPage() {
  const [items, setItems] = useState<CorkBoardItem[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [maxZIndex, setMaxZIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [groupPages, setGroupPages] = useState<GroupPage[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadItems();
    
    const channel = supabase
      .channel('cork_board_changes_admin')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cork_board_items' },
        () => {
          loadItems();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadItems = async () => {
    const { data, error } = await supabase
      .from('cork_board_items')
      .select('*')
      .order('z_index', { ascending: true });

    if (error) {
      console.error('Error loading items:', error);
      return;
    }

    if (data) {
      setItems(data);
      const maxZ = Math.max(...data.map(item => item.z_index), 0);
      setMaxZIndex(maxZ);
    }
  };

  const loadGroupPages = async () => {
    setLoadingPages(true);
    try {
      const { data, error } = await supabase
        .from('cork_board_items')
        .select('group_name, created_at, password')
        .not('group_name', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading group pages:', error);
        return;
      }

      if (data) {
        // 그룹별로 아이템 개수 집계
        const groupMap = new Map<string, { created_at: string; count: number; password: string | null }>();
        
        data.forEach(item => {
          if (item.group_name) {
            if (!groupMap.has(item.group_name)) {
              groupMap.set(item.group_name, {
                created_at: item.created_at,
                count: 1,
                password: item.password || null
              });
            } else {
              const existing = groupMap.get(item.group_name)!;
              groupMap.set(item.group_name, {
                ...existing,
                count: existing.count + 1,
                password: existing.password || item.password || null
              });
            }
          }
        });

        const pages: GroupPage[] = Array.from(groupMap.entries()).map(([name, info]) => ({
          group_name: name,
          created_at: info.created_at,
          item_count: info.count,
          password: info.password
        }));

        setGroupPages(pages);
      }
    } finally {
      setLoadingPages(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    loadGroupPages();
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleGoToPage = (groupName: string) => {
    window.open(`/${groupName}`, '_blank');
  };

  const handleDeletePage = async (groupName: string) => {
    const confirmDelete = window.confirm(`"${groupName}" 그룹의 모든 메모와 사진을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`);
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('cork_board_items')
        .delete()
        .eq('group_name', groupName);

      if (error) {
        console.error('Error deleting group:', error);
        alert('삭제에 실패했습니다. 다시 시도해주세요.');
        return;
      }

      alert(`"${groupName}" 그룹이 삭제되었습니다.`);
      loadGroupPages();
    } catch (err) {
      console.error('Error:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleMouseDown = (e: React.MouseEvent, itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    e.stopPropagation();
    setDraggingId(itemId);
    setDragOffset({
      x: e.clientX - item.position_x,
      y: e.clientY - item.position_y
    });

    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    
    updateItemZIndex(itemId, newZIndex);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingId) {
      const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - 250));
      const newY = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - 250));

      setItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    }
  };

  const handleMouseUp = async () => {
    if (draggingId) {
      const item = items.find(i => i.id === draggingId);
      if (item) {
        await updateItemPosition(draggingId, item.position_x, item.position_y);
      }
      setDraggingId(null);
    }
  };

  const handleTouchStart = (e: React.TouchEvent, itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const touch = e.touches[0];
    setDraggingId(itemId);
    setDragOffset({
      x: touch.clientX - item.position_x,
      y: touch.clientY - item.position_y
    });

    const newZIndex = maxZIndex + 1;
    setMaxZIndex(newZIndex);
    updateItemZIndex(itemId, newZIndex);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (draggingId && e.touches.length === 1) {
      const touch = e.touches[0];
      const newX = Math.max(0, Math.min(touch.clientX - dragOffset.x, window.innerWidth - 250));
      const newY = Math.max(0, Math.min(touch.clientY - dragOffset.y, window.innerHeight - 250));

      setItems(prev =>
        prev.map(item =>
          item.id === draggingId
            ? { ...item, position_x: newX, position_y: newY }
            : item
        )
      );
    }
  };

  const handleTouchEnd = async () => {
    if (draggingId) {
      const item = items.find(i => i.id === draggingId);
      if (item) {
        await updateItemPosition(draggingId, item.position_x, item.position_y);
      }
      setDraggingId(null);
    }
  };

  const updateItemZIndex = async (itemId: string, zIndex: number) => {
    await supabase
      .from('cork_board_items')
      .update({ z_index: zIndex })
      .eq('id', itemId);
  };

  const updateItemPosition = async (itemId: string, x: number, y: number) => {
    await supabase
      .from('cork_board_items')
      .update({
        position_x: x,
        position_y: y
      })
      .eq('id', itemId);
  };

  const handleDelete = async (itemId: string) => {
    const confirmDelete = window.confirm('정말 삭제하시겠습니까?');
    if (!confirmDelete) return;

    const { error } = await supabase
      .from('cork_board_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Error deleting item:', error);
      alert('삭제에 실패했습니다. 다시 시도해주세요.');
      return;
    }

    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  // 패턴 SVG 생성 함수
  const getPatternSVG = (pattern: string, color: string) => {
    const patternColor = color === '#fef08a' ? '#fde047' : 
                        color === '#fecdd3' ? '#fda4af' :
                        color === '#fed7aa' ? '#fdba74' :
                        color === '#bbf7d0' ? '#86efac' :
                        color === '#bfdbfe' ? '#93c5fd' :
                        '#d8b4fe';

    switch (pattern) {
      case 'heart':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12c-1.5-3-5-4-7-2s-2 5 0 7l7 7 7-7c2-2 2-5 0-7s-5.5-1-7 2z" 
                  fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      case 'star':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 10l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z" 
                  fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      case 'flower':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="15" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="25" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="20" cy="25" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="15" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
            <circle cx="20" cy="20" r="2" fill="${patternColor}" opacity="0.5"/>
          </svg>
        `)}`;
      case 'dot':
        return `data:image/svg+xml,${encodeURIComponent(`
          <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="3" fill="${patternColor}" opacity="0.3"/>
          </svg>
        `)}`;
      default:
        return 'none';
    }
  };

  // PC 버전 렌더링
  if (!isMobile) {
    return (
      <div
        className="w-full h-screen overflow-hidden relative cursor-default select-none"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7ec64f7075a83e09c793ffae81f699dc.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          touchAction: 'none'
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Admin Badge */}
        <div 
          className="fixed top-4 left-4 md:top-8 md:left-8 bg-red-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-full shadow-2xl flex items-center gap-2 z-50 text-sm md:text-base cursor-pointer hover:bg-red-700 transition-colors"
          onClick={handleOpenModal}
        >
          <i className="ri-shield-user-line text-lg md:text-xl" />
          <span className="font-semibold">관리자 모드</span>
        </div>

        {/* Modal */}
        {showModal && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]"
            onClick={handleCloseModal}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <i className="ri-folder-line text-red-600" />
                  그룹 페이지 관리
                </h2>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={handleCloseModal}
                >
                  <i className="ri-close-line text-2xl text-gray-600" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                {loadingPages ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent" />
                  </div>
                ) : groupPages.length === 0 ? (
                  <div className="text-center py-20">
                    <i className="ri-folder-open-line text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-400 text-lg">생성된 그룹 페이지가 없습니다</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {groupPages.map((page) => (
                      <div
                        key={page.group_name}
                        className="bg-gray-50 rounded-xl p-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1 flex items-center gap-3">
                          {page.password && (
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-lock-fill text-red-600 text-lg" />
                            </div>
                          )}
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-1">
                              {page.group_name}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <i className="ri-sticky-note-line" />
                                {page.item_count}개 항목
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-calendar-line" />
                                {new Date(page.created_at).toLocaleDateString('ko-KR')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium whitespace-nowrap flex items-center gap-2"
                            onClick={() => window.open(`/${page.group_name}?admin=true`, '_blank')}
                          >
                            <i className="ri-external-link-line" />
                            바로가기
                          </button>
                          <button
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                            onClick={() => handleDeletePage(page.group_name)}
                          >
                            <i className="ri-delete-bin-line" />
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {items.map(item => (
          <div
            key={item.id}
            className="absolute cursor-move"
            style={{
              left: `${item.position_x}px`,
              top: `${item.position_y}px`,
              zIndex: item.z_index,
              transform: `rotate(${item.rotation}deg)`
            }}
            onMouseDown={(e) => handleMouseDown(e, item.id)}
            onTouchStart={(e) => handleTouchStart(e, item.id)}
          >
            {item.type === 'note' ? (
              <div className="relative group">
                <div 
                  className="w-48 h-48 shadow-lg p-4 relative"
                  style={{ 
                    backgroundColor: item.color || '#fef08a',
                    backgroundImage: item.pattern && item.pattern !== 'none' 
                      ? `url("${getPatternSVG(item.pattern, item.color || '#fef08a')}")` 
                      : 'none',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '40px 40px'
                  }}
                >
                  <div
                    className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-6 bg-black/5"
                    style={{
                      clipPath: 'polygon(10% 0%, 90% 0%, 95% 100%, 5% 100%)'
                    }}
                  />
                  <div
                    className="w-full h-full text-gray-800 text-base leading-relaxed overflow-hidden break-words whitespace-pre-wrap"
                    style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                  >
                    {item.content || ''}
                  </div>
                </div>
                <button
                  className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center text-lg font-bold transition-all hover:scale-110 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="relative group">
                <div className="bg-white p-3 shadow-xl w-[220px]">
                  <img
                    src={item.photo_url}
                    alt="Polaroid"
                    className="w-52 h-52 object-contain bg-gray-50"
                    draggable={false}
                  />
                  <div 
                    className="h-12 bg-white flex items-center justify-center px-2 w-52"
                  >
                    <span 
                      className="text-gray-700 text-sm text-center line-clamp-2 w-full break-words"
                      style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                    >
                      {item.caption || ''}
                    </span>
                  </div>
                </div>
                <button
                  className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center text-lg font-bold transition-all hover:scale-110 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item.id);
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // 모바일 버전 렌더링
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 
            className="text-xl font-bold text-red-600 flex items-center gap-2 cursor-pointer"
            onClick={handleOpenModal}
          >
            <i className="ri-shield-user-line" />
            관리자 모드
          </h1>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-folder-line text-red-600" />
                그룹 페이지 관리
              </h2>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={handleCloseModal}
              >
                <i className="ri-close-line text-xl text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-60px)]">
              {loadingPages ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent" />
                </div>
              ) : groupPages.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-folder-open-line text-5xl text-gray-300 mb-3" />
                  <p className="text-gray-400">생성된 그룹 페이지가 없습니다</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {groupPages.map((page) => (
                    <div
                      key={page.group_name}
                      className="bg-gray-50 rounded-xl p-3"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {page.password && (
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-lock-fill text-red-600 text-base" />
                          </div>
                        )}
                        <h3 className="text-base font-semibold text-gray-800">
                          {page.group_name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-sticky-note-line" />
                          {page.item_count}개
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {new Date(page.created_at).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap cursor-pointer"
                          onClick={() => window.open(`/${page.group_name}?admin=true`, '_blank')}
                        >
                          <i className="ri-external-link-line" />
                          바로가기
                        </button>
                        <button
                          className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1 whitespace-nowrap cursor-pointer"
                          onClick={() => handleDeletePage(page.group_name)}
                        >
                          <i className="ri-delete-bin-line" />
                          삭제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 메모/사진 리스트 */}
      <div className="p-4 space-y-4 pb-20 touch-pan-y">
        {items.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-sticky-note-line text-6xl text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">등록된 메모나 사진이 없습니다</p>
          </div>
        ) : (
          items.map(item => (
            <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              {item.type === 'note' ? (
                <div 
                  className="p-6 relative"
                  style={{ 
                    backgroundColor: item.color || '#fef08a',
                    backgroundImage: item.pattern && item.pattern !== 'none' 
                      ? `url("${getPatternSVG(item.pattern, item.color || '#fef08a')}")` 
                      : 'none',
                    backgroundRepeat: 'repeat',
                    backgroundSize: '40px 40px'
                  }}
                >
                  <div
                    className="text-gray-800 text-lg leading-relaxed whitespace-pre-wrap break-words min-h-[100px]"
                    style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                  >
                    {item.content || ''}
                  </div>
                  <button
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full shadow-lg flex items-center justify-center text-lg font-bold cursor-pointer"
                    onClick={() => handleDelete(item.id)}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={item.photo_url}
                    alt="Photo"
                    className="w-full h-64 object-cover"
                  />
                  {item.caption && (
                    <div 
                      className="p-4 bg-white text-center"
                      style={{ fontFamily: "'Nanum Pen Script', cursive" }}
                    >
                      <p className="text-gray-700 text-base line-clamp-2 break-words">{item.caption}</p>
                    </div>
                  )}
                  <button
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full shadow-lg flex items-center justify-center text-lg font-bold cursor-pointer"
                    onClick={() => handleDelete(item.id)}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
